import React, { useState } from 'react';
import './CategoryPage.css';
import SearchBar from './SearchBar';
import SearchResults from './SearchResults';
import Pagination from './Pagination';
import CategoryIcon, { icons } from './CategoryIcon';

const CategoryPage = ({
  currentCategory,
  songs,
  searchQuery,
  setSearchQuery,
  searchResults,
  config,
  onSongClick,
  currentPage,
  totalPages,
  paginationInfo,
  onPageChange,
  categorySongCounts,
  onCategoryClick,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const handlePageChange = (page) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    onPageChange(page);
  };

  const handleNavBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    } else {
      onCategoryClick('home');
    }
  };

  const toggleSearch = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (isSearchExpanded) {
      setSearchQuery('');
    }
  };

  return (
    <main className="category-page">
      <div className="category-container">

        {/* Top row: Nav buttons + Search toggle */}
        <div className="category-top-row">
          <div className="category-nav-group">
            <button
              className="category-back-btn"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              aria-label="Toggle categories menu"
              title="Browse Categories"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>

            <button
              className="category-back-btn"
              onClick={handleNavBack}
              aria-label={currentPage > 1 ? 'Go to previous page' : 'Go to Home'}
            >
              {currentPage > 1 ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M5 12l7-7M5 12l7 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Back
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12L12 3l9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 21V12h6v9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5 10v11h14V10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Home
                </>
              )}
            </button>
          </div>

          {/* Desktop inline search — hidden on mobile */}
          <div className="category-search-container desktop-search">
            {(isSearchExpanded || searchQuery) ? (
              <div className="category-search-wrap">
                <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                <button className="search-close-btn" onClick={toggleSearch}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ) : (
              <button className="category-search-toggle" onClick={() => setIsSearchExpanded(true)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            )}
          </div>

          {/* Mobile search toggle icon — shown only on mobile */}
          <button
            className="category-search-toggle mobile-search-toggle"
            onClick={() => setIsSearchExpanded(!isSearchExpanded)}
            aria-label="Toggle search"
          >
            {(isSearchExpanded || searchQuery) ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile full-width search bar — slides down below the top row */}
        <div className={`mobile-search-bar-wrap ${(isSearchExpanded || searchQuery) ? 'open' : ''}`}>
          <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
        </div>


        {/* Search Results */}
        <SearchResults searchResults={searchResults} onSongClick={onSongClick} />

        {/* Category Header */}
        {!searchQuery && (
          <header className="category-header">
            {/* Shimmer overlay */}
            <div className="category-header-shimmer" />

            <div className="category-header-content">
              <span className="category-icon category-header-icon">
                {icons[currentCategory?.id] ? (
                  <CategoryIcon categoryId={currentCategory?.id} size={52} />
                ) : currentCategory?.icon?.startsWith('/') ? (
                  <img src={currentCategory.icon} alt={currentCategory.title} className="category-header-icon-img" />
                ) : (
                  currentCategory?.icon
                )}
              </span>
              <h1 className="category-title">{currentCategory?.title}</h1>
            </div>
            <p className="category-description">
              {currentCategory?.description}
              {config?.ui?.showSongCount && categorySongCounts && (
                <span className="song-count">
                  {' '}• {categorySongCounts[currentCategory?.id] || 0} songs available
                </span>
              )}
            </p>
          </header>
        )}

        {/* Songs List */}
        {!searchQuery && (
          <div className="songs-list">
            {songs.map((song, index) => (
              <div
                key={song.id}
                className={`song-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                onClick={() => onSongClick(song)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onSongClick(song)}
              >
                {/* Hover shimmer overlay */}
                <div className="song-overlay"></div>

                <div className="song-content">
                  <div className="song-main">
                    {/* Song Number + Title */}
                    <div className="song-number-title">
                      <div className={`song-number-badge ${index % 2 === 0 ? 'blue' : 'purple'}`}>
                        {song.songNumber}
                      </div>
                      <h3 className="song-title tamil-text">
                        {song.title ? (
                          <>
                            {song.title}
                            {song.englishTitle && (
                              <span className="english-title"> — {song.englishTitle}</span>
                            )}
                          </>
                        ) : (
                          song.englishTitle || 'Untitled'
                        )}
                      </h3>
                    </div>

                    {/* Artist / Duration */}
                    <div className="song-meta">
                      {song.artist && (
                        <span className="song-artist">
                          <svg viewBox="0 0 24 24" fill="none">
                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor" />
                          </svg>
                          {song.artist}
                        </span>
                      )}
                      {song.artist && song.duration && <span className="meta-separator">•</span>}
                      {song.duration && (
                        <span className="song-duration">
                          <svg viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                            <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                          {song.duration}
                        </span>
                      )}
                    </div>

                    {/* Tags */}
                    {song.tags && song.tags.length > 0 && (
                      <div className="song-tags">
                        {song.tags.slice(0, 3).map((tag, tagIndex) => (
                          <span key={tagIndex} className={`song-tag ${index % 2 === 0 ? 'blue' : 'purple'}`}>
                            {tag}
                          </span>
                        ))}
                        {song.tags.length > 3 && (
                          <span className="song-tag gray">+{song.tags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Play / Coming Soon */}
                  <div className="song-status">
                    {song.youtubeId ? (
                      <div className={`play-button ${index % 2 === 0 ? 'blue' : 'purple'}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                          <path d="M8 5v14l11-7z" fill="currentColor" />
                        </svg>
                      </div>
                    ) : (
                      <div className="coming-soon">
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor" />
                        </svg>
                        Soon
                      </div>
                    )}
                  </div>
                </div>

                {/* Tooltip */}
                {!song.youtubeId && (
                  <div className="tooltip">Video will be uploaded shortly</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination — Bottom */}
        {!searchQuery && totalPages > 1 && (
          <div className="pagination-bottom">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              paginationInfo={paginationInfo}
            />
          </div>
        )}

        {/* Empty State */}
        {!searchQuery && songs.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">🎵</div>
            <p className="empty-title">No songs found in this category.</p>
            <p className="empty-message">Try selecting a different category or use the search feature.</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="category-footer">
        <p className="footer-text">© 2026 {config?.app?.name || 'Idhaya Ragam'} — Christian Songs Collection</p>
        <p className="footer-note">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58zM9.75 15.02V8.98L15.5 12l-5.75 3.02z" fill="currentColor" />
          </svg>
          Click on any song to watch on YouTube
        </p>
      </footer>
    </main>
  );
};

export default CategoryPage;
